import React, { useState, useRef } from 'react';
import EXIF from 'exif-js'; // Asegúrate de haber instalado 'exif-js'

// --- Estilos CSS (Se recomienda usar un archivo CSS separado, pero se incluyen aquí para completitud) ---
const styles = {
    body: {
        fontFamily: 'Segoe UI, sans-serif',
        backgroundColor: '#f4f4f9',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        color: '#333',
        minHeight: '100vh',
        margin: 0,
    },
    card: {
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        textAlign: 'center',
    },
    h2: { marginTop: 0, color: '#2c3e50' },
    p: { color: '#666', fontSize: '0.9em' },
    btnCapture: {
        backgroundColor: '#007bff',
        color: 'white',
        padding: '15px 20px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        transition: 'background 0.2s',
    },
    btnCaptureActive: { backgroundColor: '#0056b3' },
    previewContainer: {
        marginTop: '20px',
    },
    imgPreview: {
        width: '100%',
        borderRadius: '8px',
        border: '2px solid #eee',
    },
    metadataBox: {
        marginTop: '15px',
        textAlign: 'left',
        background: '#f8f9fa',
        borderRadius: '8px',
        padding: '10px',
        fontSize: '0.85em',
        border: '1px solid #e9ecef',
    },
    metaRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        borderBottom: '1px solid #eee',
    },
    metaLabel: { fontWeight: 'bold', color: '#555' },
    metaValue: { color: '#007bff', fontFamily: 'monospace' },
    securityBadge: {
        marginTop: '10px',
        padding: '5px',
        borderRadius: '4px',
        fontSize: '0.8em',
    },
    badgeSuccess: {
        background: '#e8f5e9',
        color: '#2e7d32',
    },
    badgeWarning: {
        background: '#ffebee',
        color: '#c62828',
    }
};
// --- Fin de Estilos CSS ---

const DniCaptureComponent = () => {
    // Referencia al input de archivo oculto
    const inputRef = useRef(null);

    // Estados para gestionar los datos de la imagen y metadatos
    const [imageSrc, setImageSrc] = useState(null);
    const [metadata, setMetadata] = useState([]);
    const [securityStatus, setSecurityStatus] = useState({
        message: "Extrayendo datos forenses...",
        type: 'initial', // 'initial', 'success', 'warning'
    });

    // Función para simular el click en el input de archivo
    const triggerCamera = () => {
        inputRef.current.click();
    };

    /**
     * Procesa el archivo de imagen seleccionado.
     * @param {Event} e - Evento de cambio del input de archivo.
     */
    const handleFileChange = (e) => {
        const file = e.target.files[0];

        if (file) {
            // 1. Mostrar Previsualización
            const objectURL = URL.createObjectURL(file);
            setImageSrc(objectURL);

            // Limpiar y preparar estados
            setMetadata([]);
            setSecurityStatus({ message: "Analizando archivo...", type: 'initial' });

            // 2. Extraer Metadata EXIF con la librería
            const reader = new FileReader();

            // La librería EXIF.js necesita la imagen en un tag <img> o como Buffer/ArrayBuffer.
            // Aquí usamos un objeto Image para que EXIF.getData() pueda procesarlo.
            reader.onload = function(e) {
                const img = document.createElement("img");
                img.src = e.target.result;

                img.onload = function() {
                    EXIF.getData(img, function() {
                        const allMetaData = EXIF.getAllTags(this);
                        displayMetadata(allMetaData, file);
                    });
                };
            };

            reader.readAsDataURL(file);
        }
    };

    /**
     * Procesa y muestra los metadatos forenses.
     * @param {object} tags - Metadatos EXIF.
     * @param {File} file - Objeto File con información del archivo.
     */
    const displayMetadata = (tags, file) => {
        // Datos clave para forensia
        const criticalData = [
            { label: "Cámara (Make)", value: tags.Make || "Desconocido" },
            { label: "Modelo", value: tags.Model || "Desconocido" },
            { label: "Software", value: tags.Software || "Original (Sin editar)" },
            { label: "Fecha Original", value: tags.DateTimeOriginal || "No encontrada" },
            { label: "ISO", value: tags.ISOSpeedRatings || "-" },
            { label: "Tamaño", value: (file.size / 1024 / 1024).toFixed(2) + " MB" }
        ];

        let hasExif = false;
        const processedMetadata = criticalData.map(item => {
            let value = item.value;
            let style = {};

            if (value !== "Desconocido" && value !== "-" && value !== "No encontrada" && item.label !== "Tamaño") {
                hasExif = true;
            }

            // Resaltar si detectamos software de edición (ej: Photoshop)
            if(item.label === "Software" && (value.includes("Adobe") || value.includes("GIMP") || value.includes("Photoshop"))) {
                style = { color: 'red', fontWeight: 'bold' };
                value += " ⚠️ (POSIBLE EDICIÓN)";
            }

            return {
                ...item,
                value,
                style
            };
        });

        setMetadata(processedMetadata);

        if (hasExif) {
            setSecurityStatus({
                message: "✅ Metadata de Hardware detectada (Archivo Original)",
                type: 'success'
            });
        } else {
            setSecurityStatus({
                message: "⚠️ Advertencia: Sin metadata (Posible imagen web/captura pantalla)",
                type: 'warning'
            });
        }
    };

    // Determinar los estilos del badge de seguridad
    const badgeStyle = securityStatus.type === 'success'
        ? styles.badgeSuccess
        : securityStatus.type === 'warning'
            ? styles.badgeWarning
            : {};

    // Renderizado del componente
    return (
        <div style={styles.body}>
            <div style={styles.card}>
                <h2 style={styles.h2}>Verificación DNI</h2>
                <p style={styles.p}>Captura el reverso del documento usando la cámara nativa para validar integridad.</p>

                {/* Input de archivo real, oculto al usuario */}
                <input
                    type="file"
                    ref={inputRef}
                    id="native-camera-input"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />

                <button
                    style={styles.btnCapture}
                    onClick={triggerCamera}
                    // Nota: Los estilos :active se manejan mejor con CSS o un estado de React si se necesita una retroalimentación visual más compleja.
                >
                    📷 Activar Cámara Trasera
                </button>

                {/* Área de Previsualización y Metadatos */}
                {imageSrc && (
                    <div style={styles.previewContainer}>
                        <img
                            src={imageSrc}
                            style={styles.imgPreview}
                            alt="Vista previa DNI"
                        />

                        <div
                            style={{ ...styles.securityBadge, ...badgeStyle }}
                        >
                            {securityStatus.message}
                        </div>

                        <div style={styles.metadataBox}>
                            {metadata.length > 0 ? (
                                metadata.map((item, index) => (
                                    <div key={index} style={styles.metaRow}>
                                        <span style={styles.metaLabel}>{item.label}</span>
                                        <span style={{ ...styles.metaValue, ...item.style }}>{item.value}</span>
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign:'center', color:'#999' }}>
                                    {securityStatus.message}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DniCaptureComponent;
